import { getApiBaseUrl } from '../lib/api';
import React, { useEffect, useState } from 'react';
import { ArrowLeft, FileText, Calendar } from 'lucide-react';
import { Project, TabType } from '../types';
import { Sidebar } from './Sidebar';
import { TabContent } from './TabContent';
import { TaskerProject } from './TaskerProject';
import { getAuthToken } from '../lib/supabase';

interface MainProjectProps {
  project: Project;
  onBack: () => void;
}

export const MainProject: React.FC<MainProjectProps> = ({ project, onBack }) => {
  const [activeTab, setActiveTab] = useState<TabType>('interface');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [role, setRole] = useState<Project['role'] | undefined>(project.role);
  const [previewReviewLevel, setPreviewReviewLevel] = useState<number | undefined>(undefined);

  // Load role from backend; this ensures stable gating independent of URL flags
  useEffect(() => {
    (async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;
        const API_URL = getApiBaseUrl();
        const resp = await fetch(`${API_URL}/api/projects/${project.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data?.role) setRole(data.role);
        }
      } catch {}
    })();
  }, [project.id]);

  // Listen for requests from other components to open the Interface tab
  useEffect(() => {
    const handler = (e: Event) => {
      const anyEvent = e as unknown as { detail?: { reviewLevel?: number; taskId?: string } };
      const level = anyEvent?.detail?.reviewLevel;
      if (typeof level === 'number') {
        setPreviewReviewLevel(level);
      }
      // Forward taskId via a global the iframe can read if needed
      const taskId = anyEvent?.detail?.taskId;
      if (taskId) {
        (window as any).__PREVIEW_TASK_ID__ = taskId;
      }
      setActiveTab('interface');
    };
    window.addEventListener('open-interface', handler as EventListener);
    return () => window.removeEventListener('open-interface', handler as EventListener);
  }, []);

  // Student view if role is attemptor/reviewer OR when routed via /desk/:id (admin preview-as-annotator)
  const isDeskPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/desk');
  const isTaskerView = isDeskPath || role === 'attemptor' || role === 'reviewer';

  if (isTaskerView) {
    return <TaskerProject project={project} onBack={onBack} />;
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="h-screen flex flex-col bg-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur px-4 py-2.5 border-b border-zinc-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="flex items-center text-zinc-600 hover:text-zinc-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              <span className="text-sm">Back to Projects</span>
            </button>
            <div className="w-px h-5 bg-zinc-300"></div>
            <div className="flex items-center space-x-2.5">
              <div className="w-8 h-8 bg-zinc-100 rounded-md flex items-center justify-center">
                <FileText className="w-4 h-4 text-zinc-800" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-zinc-900 leading-tight">{project.name}</h1>
                <div className="flex items-center text-xs text-zinc-500">
                  <Calendar className="w-3.5 h-3.5 mr-1" />
                  Updated {formatDate(project.updatedAt)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <Sidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          role={role}
        />

        {/* Content Area */}
        <main className="flex-1 overflow-auto">
          <div className="h-full">
            <TabContent activeTab={activeTab} project={project} role={role} previewReviewLevel={previewReviewLevel} />
          </div>
        </main>
      </div>
    </div>
  );
};